import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, ArrowLeft, Maximize2, Minimize2 } from "lucide-react";
import { TspCard } from "../../../components/primitives";
import TspLoadError from "../../../components/common/TspLoadError";
import TspMap from "../../../components/map/TspMap";
import TspMapPointPanel from "../../../components/map/TspMapPointPanel";
import TspApiService from "../../../api/tspApiService";
import useTspApiResource from "../../../hooks/useTspApiResource";
import useTspPermission from "../../../hooks/useTspPermission";
import useTspReverseGeocode from "../../../hooks/useTspReverseGeocode";
import TSP_PERMISSIONS from "../../../constants/tspPermissions";
import { LIST_STATUS_FILTERS } from "../../../constants/tspJourneyStatus";
import { pointToSearch, toPoint } from "../../../utils/tspCoordinates";
import useTspStateBoundaries from "../useTspStateBoundaries";
import { boundsOf, featuresBounds, filterMapPlaces, pointKeyForPlace, selectionForFeature, selectionForState, stackPlacePoints, stateDetail, visiblePoints } from "../coverageMapModel";
import CoveragePlaceList from "./CoveragePlaceList";
import CoverageStatePanel from "./CoverageStatePanel";
import CoverageLocationSearch from "./CoverageLocationSearch";

const pad2 = (value) => String(Number(value) || 0).padStart(2, "0");

// The selected journey place in the page URL, so a refresh keeps it (ids only).
const PLACE_PARAM = "map_place";

// "Sankirtan Coverage Across India": a real map (MapLibre + OpenStreetMap tiles,
// Survey of India state outlines) of JOURNEY PLACES — every place added to a
// journey is its own marker, at the coordinates saved for it (its visit's GPS,
// else the point chosen on Add Journey) — beside the list of those places
// ("<Taluka / Town> - <Temple>", with each place's date, grouped by year).
//
//   on load (no place chosen, none in the URL) -> EVERY listed place at once;
//   nearby markers cluster, and places at the very same spot share one marker
//   that lists each of them. Clicking a marker shows THAT place.
//   click a place in the list -> it is selected and the map zooms to it. Click
//   it again (or "All Places") -> back to every place. A state (on the map, or
//   in the list) -> the state is highlighted and the map zooms to it.
//   search a city / district / state -> the map flies there; markers unchanged.
//
// The state outline is navigation only. Every marker comes from GET
// /tsp/map/coverage (loaded once per year) — one request, whatever the number
// of places. A journey's CURRENT place (server-derived: its latest visit, while
// in progress) is a pulsing marker. `coverage` holds the Journeys summary's
// counters; `filters` are the Journeys screen's filters.
//
// Map -> Create Journey (journeys.manage only): a click anywhere on the map
// selects that exact point (a distinct pin) — no mode to turn on — the server
// resolves it to state / city / area, and "Create Journey" opens the ordinary
// Add Journey form (/tsp/journeys/new?lat=&lng=) pre-filled with it. Nothing is
// saved until that form is submitted; closing or picking again forgets the point.
// For these users a click on a state outline picks a point too, so states open
// from the States list and the clusters; everyone else clicks a state as before.
//
// Year-wise history: `year` (the Journeys filter) is sent to the server
// (?year=), which returns only that year's journeys, visits and stops; empty
// means All Years. Visits of ended journeys carry a distinct historical marker.
export default function JourneyCoverageMap({ coverage = {}, year = "", filters = null }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPlaceId = Number(searchParams.get(PLACE_PARAM)) || null;
  const { hasPermission } = useTspPermission();
  const canCreateJourney = hasPermission(TSP_PERMISSIONS.JOURNEYS_MANAGE);
  const map = useTspApiResource(() => TspApiService.getMapCoverage(year ? { year } : undefined), [year], { refreshMs: 60000 });
  const data = map.data;
  const { states: boundaries, national } = useTspStateBoundaries();

  const [chosenPlaceId, setPlaceId] = useState(null); // the journey place last chosen (list, state panel)
  const [clickedKey, setClickedKey] = useState(null); // a marker clicked on the map
  const [selection, setSelection] = useState(null); // the open state: { key, name, stateIds, lgdCodes }
  const [isStatePanelOpen, setIsStatePanelOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [notice, setNotice] = useState("");
  const [pickedPoint, setPickedPoint] = useState(null);
  const [searchedPlace, setSearchedPlace] = useState(null); // the place found with the location search, pinned
  const geocode = useTspReverseGeocode(pickedPoint, { enabled: canCreateJourney });

  // Another year is another set of journeys: forget the previous year's
  // selection, so nothing of it is shown, or acted on, while the new year loads.
  useEffect(() => {
    setPlaceId(null);
    setClickedKey(null);
    setSelection(null);
    setIsStatePanelOpen(false);
    setNotice("");
  }, [year]);

  const filterQ = filters?.q || "";
  const filterTemple = filters?.temple || "";
  const filterStateId = filters?.stateId || "";
  // Active / Completed / Cancelled -> the journey statuses the list shows (All -> none).
  const filterStatuses = LIST_STATUS_FILTERS[filters?.status]?.query || "";
  const listed = useMemo(
    () => (data ? filterMapPlaces(data, { q: filterQ, temple: filterTemple, stateId: filterStateId, statuses: filterStatuses }) : []),
    [data, filterQ, filterTemple, filterStateId, filterStatuses]
  );

  // The selected place: the one chosen while it is listed, else the place in
  // the URL when listed, else none — the map then shows every listed place.
  const placeId = useMemo(() => {
    const isListed = (id) => listed.some((row) => row.place_id === id);
    if (isListed(chosenPlaceId)) return chosenPlaceId;
    return isListed(urlPlaceId) ? urlPlaceId : null;
  }, [listed, chosenPlaceId, urlPlaceId]);
  const selectedPlace = listed.find((row) => row.place_id === placeId) || null;

  // Keep the URL on the selected place (replace: no history entry per click).
  useEffect(() => {
    if (!placeId || urlPlaceId === placeId) return;
    const next = new URLSearchParams(searchParams);
    next.set(PLACE_PARAM, String(placeId));
    setSearchParams(next, { replace: true });
  }, [placeId, urlPlaceId, searchParams, setSearchParams]);

  // Escape steps back one level: the selected point, then full screen.
  useEffect(() => {
    if (!isFullscreen && !pickedPoint) return undefined;
    const handleKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (pickedPoint) setPickedPoint(null);
      else setIsFullscreen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, pickedPoint]);

  // Someone who may not create journeys never holds a picked point.
  useEffect(() => {
    if (!canCreateJourney) setPickedPoint(null);
  }, [canCreateJourney]);

  // A new point replaces the previous one; an invalid coordinate is ignored.
  const pickPoint = (lngLat) => {
    const point = toPoint(lngLat);
    if (point) setPickedPoint(point);
  };

  // Opens the existing Add Journey form for this point, once (a double click
  // must not navigate twice). The answer already fetched travels along so the
  // form does not ask the server again.
  const creatingRef = useRef(false);
  const createJourneyHere = () => {
    if (!pickedPoint || !canCreateJourney || creatingRef.current) return;
    creatingRef.current = true;
    navigate(`/tsp/journeys/new?${pointToSearch(pickedPoint)}`, { state: geocode.data ? { mapGeocode: geocode.data } : undefined });
  };

  // Every listed place with coordinates is a marker (places at one spot share one).
  const ownPoints = useMemo(() => stackPlacePoints(listed), [listed]);
  const points = useMemo(() => visiblePoints(ownPoints, selection ? selection.stateIds : null), [ownPoints, selection]);
  // The highlighted marker: the selected place's, else the one clicked.
  const selectedKey = placeId ? pointKeyForPlace(ownPoints, placeId) : clickedKey;
  const panel = useMemo(() => (data && selection ? stateDetail(data, selection) : null), [data, selection]);
  const visitedLgdCodes = useMemo(() => {
    if (!data || !boundaries) return [];
    return [...new Set(data.states.flatMap((state) => (selectionForState(state.state_id, data, boundaries) || { lgdCodes: [] }).lgdCodes))];
  }, [data, boundaries]);

  const focusOn = (next) => setFocus({ key: `${Date.now()}-${Math.random()}`, ...next });

  // One place at a time: a click in the list selects it and zooms to it
  // (closing an open state, which might not hold it).
  const choosePlace = (id) => {
    const place = listed.find((row) => row.place_id === id);
    setPlaceId(id);
    setClickedKey(null);
    setSelection(null);
    setIsStatePanelOpen(false);
    if (place && Number.isFinite(place.latitude) && Number.isFinite(place.longitude)) {
      setNotice("");
      focusOn({ center: [place.latitude, place.longitude], zoom: 11 });
    } else {
      setNotice(`${place ? place.place_label : "This place"} has no saved map location yet, so it is not on the map.`);
    }
  };

  // Back to every place: forget the choice (and the URL's place, so a refresh
  // stays on every place), then show all of India — or, with a state open,
  // that state with every place in it.
  const forgetPlace = () => {
    setPlaceId(null);
    if (searchParams.has(PLACE_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(PLACE_PARAM);
      setSearchParams(next, { replace: true });
    }
  };
  const showAllPlaces = () => {
    forgetPlace();
    setClickedKey(null);
    setNotice("");
    if (!selection) focusOn({});
  };

  // Open a state: highlight it and zoom to its outline (or, without outlines,
  // to its visited cities), and show state details panel.
  const openSelection = (next) => {
    if (!next) return;
    setSelection(next);
    setIsStatePanelOpen(true);
    setNotice("");
    const bounds = featuresBounds(boundaries, next.lgdCodes) || boundsOf((data?.points || []).filter((point) => next.stateIds.includes(point.state_id)));
    // Keep the state clear of the state panel, which sits over the map's right side on wide screens.
    const panelSide = typeof window !== "undefined" && window.innerWidth >= 900 ? 330 : 24;
    if (bounds) focusOn({ bounds, maxZoom: 9, padding: { top: 40, bottom: 40, left: 40, right: panelSide } });
  };
  const openState = (stateId) => openSelection(selectionForState(stateId, data, boundaries));
  const openFeature = (feature) => openSelection(selectionForFeature(feature, data, boundaries));

  // Explicit back to India resets selection and zooms out to all-India bounds.
  const backToIndia = () => {
    setSelection(null);
    setIsStatePanelOpen(false);
    focusOn({});
  };

  // Close only the details modal/panel without resetting map position or zoom level.
  const handleCloseStatePanel = () => {
    setIsStatePanelOpen(false);
  };

  // A marker click shows that place (or the places at that spot) in its popup.
  const selectPoint = (point) => {
    setClickedKey(point.key);
    if (placeId && !(point.places || [point]).some((member) => member.place_id === placeId)) forgetPlace();
    setNotice("");
  };

  // A searched place: fly to it (its outline / extent, else its point). The
  // markers, the chosen journey and an open state are left exactly as they were.
  // It is also pinned (its district's point when the town itself was not found).
  const locatePlace = (place) => {
    setSearchedPlace({
      latitude: place.latitude,
      longitude: place.longitude,
      label: place.approximate && place.district_name ? `${place.district_name} district` : place.name,
    });
    const maxZoom = place.max_zoom || 10;
    if (place.bounds) focusOn({ bounds: place.bounds, maxZoom, padding: 40 });
    else focusOn({ center: [place.latitude, place.longitude], zoom: maxZoom });
  };

  // A city chosen in the state panel: the listed marker there.
  const selectStateCity = (locationId) => {
    const point = ownPoints.find((candidate) => (candidate.places || [candidate]).some((member) => member.location_id === locationId));
    if (!point) {
      setNotice("No listed journey place at this town has a saved map location.");
      return;
    }
    selectPoint(point);
    focusOn({ center: [point.latitude, point.longitude], zoom: 11 });
  };

  // A journey chosen in the state panel: its first listed place in that state.
  const selectStateJourney = (journeyId) => {
    const place = listed.find((row) => row.journey_id === journeyId && (!selection || selection.stateIds.includes(row.state_id))) || listed.find((row) => row.journey_id === journeyId);
    if (place) choosePlace(place.place_id);
  };

  // Why the map has no marker: nothing recorded at all, no place listed, none of
  // them has a saved location, or none in the open state. Never fake markers.
  const emptyMessage = (() => {
    if (!data) return null;
    if ((data.places || []).length === 0) return year ? `No journey place dated ${year} yet.` : "No journey places yet. Places appear here as soon as a journey is created.";
    if (listed.length === 0) return "No journey place on the map matches these filters.";
    if (ownPoints.length === 0) return "None of these journey places has a saved map location yet.";
    if (points.length === 0 && selection) return `No listed journey place in ${selection.name} has a saved map location.`;
    return null;
  })();

  const counters = [
    { label: "Upcoming Visits", value: pad2(coverage.upcoming_visits) },
    { label: "Talukas / Towns Reached", value: pad2(coverage.cities_reached) },
    { label: "States Covered", value: pad2(coverage.states_covered) },
  ];

  const renderBody = () => {
    if (map.error) return <TspLoadError error={map.error} onRetry={map.reload} what="the coverage map" />;
    if (!data) return <div className="tsp-cov-loading">Loading map…</div>;
    return (
      <div className="tsp-cov-layout">
        <CoveragePlaceList places={listed} placeId={placeId} onSelect={choosePlace} onShowAll={showAllPlaces} filtered={listed.length !== (data.places || []).length} />

        <div className={`tsp-cov-map-column${isFullscreen ? " is-fullscreen" : ""}`}>
          <div className="tsp-cov-toolbar">
            <div className="tsp-cov-toolbar-main">
              {selection ? (
                <>
                  <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm" onClick={backToIndia}>
                    <ArrowLeft size={13} aria-hidden="true" /> India
                  </button>
                  <strong className="tsp-cov-toolbar-title">{selection.name} — state map</strong>
                  {!isStatePanelOpen && (
                    <button type="button" className="tsp-btn tsp-btn-outline tsp-btn-sm tsp-cov-details-toggle" onClick={() => setIsStatePanelOpen(true)}>
                      State Details
                    </button>
                  )}
                </>
              ) : (
                <strong className="tsp-cov-toolbar-title">
                  {canCreateJourney ? "India overview — click the map to choose a location" : "India overview — select a state to open its map"}
                </strong>
              )}
            </div>
            <div className="tsp-cov-toolbar-actions">
              <CoverageLocationSearch onLocate={locatePlace} onClear={() => setSearchedPlace(null)} />
              <button
                type="button"
                className={`tsp-btn tsp-btn-outline tsp-btn-sm tsp-cov-fullscreen-btn${isFullscreen ? " is-active" : ""}`}
                onClick={() => setIsFullscreen((prev) => !prev)}
                title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
                aria-label={isFullscreen ? "Exit Full Screen" : "Full Screen"}
              >
                {isFullscreen ? (
                  <>
                    <Minimize2 size={13} aria-hidden="true" />
                    <span>Exit Full Screen</span>
                  </>
                ) : (
                  <>
                    <Maximize2 size={13} aria-hidden="true" />
                    <span>Full Screen</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="tsp-cov-map-frame">
            <TspMap
              points={points}
              selectedKey={selectedKey}
              onPointClick={selectPoint}
              onClusterClick={(cluster) =>
                selection && selection.stateIds.includes(cluster.stateId) ? focusOn({ bounds: boundsOf(cluster.points), maxZoom: 14 }) : openState(cluster.stateId)
              }
              boundaries={boundaries}
              nationalBoundary={national}
              visitedLgdCodes={visitedLgdCodes}
              selectedLgdCodes={selection ? selection.lgdCodes : []}
              onStateClick={openFeature}
              focus={focus}
              ariaLabel={
                selectedPlace
                  ? `Map of journey places, ${selectedPlace.place_label} selected${selection ? `, in ${selection.name}` : ""}`
                  : selection
                    ? `Map of journey places in ${selection.name}`
                    : "Map of India with journey places"
              }
              coverage={data}
              onMapPick={canCreateJourney ? pickPoint : undefined}
              pickedPoint={pickedPoint}
              searchedPlace={searchedPlace}
              pickedContent={
                pickedPoint ? (
                  <TspMapPointPanel point={pickedPoint} geocode={geocode} canCreate={canCreateJourney} onCreate={createJourneyHere} onClose={() => setPickedPoint(null)} />
                ) : null
              }
            />
            {emptyMessage && <div className="tsp-cov-map-empty">{emptyMessage}</div>}
            {panel && isStatePanelOpen && (
              <CoverageStatePanel
                detail={panel}
                onSelectCity={selectStateCity}
                onSelectJourney={selectStateJourney}
                onClose={handleCloseStatePanel}
              />
            )}
          </div>
          {notice && (
            <p className="tsp-cov-note" role="status">
              {notice}
            </p>
          )}
          {data.totals.visits_without_coordinates > 0 && (
            <p className="tsp-cov-note">
              {data.totals.visits_without_coordinates} older visit{data.totals.visits_without_coordinates === 1 ? " was" : "s were"} recorded without coordinates and{" "}
              {data.totals.visits_without_coordinates === 1 ? "is" : "are"} not shown on the map.
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <TspCard title={`Sankirtan Coverage Across India${year ? ` · ${year}` : ""}`} className="tsp-title-20 tsp-coverage-card">
      <div className="tsp-coverage-map">
        <div className="tsp-coverage-map-area tsp-coverage-map-area--live">{renderBody()}</div>

        <div className="tsp-coverage-side">
          <div className="tsp-coverage-legend-wrap">
            <span className="tsp-coverage-legend-title">Legend</span>
            <ul className="tsp-cov-legend">
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--visited" aria-hidden="true" /> Visited location
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--historical" aria-hidden="true" /> Past journey visit (completed / cancelled)
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--current" aria-hidden="true" /> Current location (pulsing)
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--upcoming" aria-hidden="true" /> Upcoming location
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--not-visited" aria-hidden="true" /> Not visited (journey ended)
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--cluster" aria-hidden="true" /> Several places — open the state
              </li>
              <li>
                <span className="tsp-cov-legend-swatch" aria-hidden="true" /> State with visits
              </li>
              <li>
                <span className="tsp-cov-legend-dot tsp-cov-legend-dot--searched" aria-hidden="true" /> Searched location
              </li>
              {canCreateJourney && (
                <li>
                  <span className="tsp-cov-legend-dot tsp-cov-legend-dot--picked" aria-hidden="true" /> Selected location (new journey)
                </li>
              )}
            </ul>
          </div>

          {data && data.states.length > 0 && (
            <div className="tsp-coverage-legend-wrap">
              <span className="tsp-coverage-legend-title">States</span>
              <ul className="tsp-cov-states" aria-label="States with visits">
                {data.states.map((state) => {
                  const active = Boolean(selection && selection.stateIds.includes(state.state_id));
                  return (
                    <li key={state.state_id}>
                      <button type="button" className={`tsp-cov-state-btn${active ? " is-active" : ""}`} aria-pressed={active} onClick={() => openState(state.state_id)}>
                        <span>{state.state_name}</span>
                        <span className="tsp-legend-badge">{state.visited_city_count}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="tsp-coverage-counters">
            {counters.map((counter) => (
              <div key={counter.label} className="tsp-coverage-counter">
                <MapPin size={18} />
                <strong>{counter.value}</strong>
                <span>{counter.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </TspCard>
  );
}
